import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

import { logout } from '../../../Config/Auth';

import YearMonthSelector from '../../Components/DashboardItems/YearMonthSelector/YearMonthSelector';
import JobsOverview from '../../Components/DashboardItems/JobsOverview/JobsOverview';
import JobsTrend from '../../Components/DashboardItems/JobsTrend/JobsTrend';
import InvoicesOverview from '../../Components/DashboardItems/InvoiceOverview/InvoiceOverview';
import InvoicesTrend from '../../Components/DashboardItems/InvoiceTrend/InvoiceTrend';
import TrucksTrend from '../../Components/DashboardItems/TruckTrend/TruckTrend';
import CCompanies from '../../Components/DashboardItems/TopList/CCompanies';

import './Dashboard.scss';
import apiClient from '../../../../../Config/apiClient';

const dashboardPath = process.env.REACT_APP_HS_DASHBOARD_BASE_PATH;

const Dashboard = () => {

  const today = new Date();
  const fiscalIndex = (today.getMonth() + 9) % 12;
  const fiscalMonthOrder = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];
  const currentFiscalMonth = fiscalMonthOrder[fiscalIndex];
  const currentFiscalYear = today.getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentFiscalYear);
  const [selectedMonth, setSelectedMonth] = useState(currentFiscalMonth);

  const [jobTrendData, setJobTrendData] = useState([]);
  const [invoiceTrendData, setInvoiceTrendData] = useState([]);
  const [truckTrendData, setTruckTrendData] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  // Invoice trend data sample
  const [invoiceData, setInvoiceData] = useState([]);
 // Invoice trend data sample end
  const [jobSummary, setJobSummary] = useState({
    MainTitle: "Jobs Overview",
    cardDetails: [
      { cardTitle: "Total", cardContent: 0 },
      { cardTitle: "Pending", cardContent: 0 },
      { cardTitle: "Completed", cardContent: 0 },
      { cardTitle: "In Progress", cardContent: 0 },
      { cardTitle: "Cancelled", cardContent: 0 }
    ]
  });

  const [invSummary, setInvSummary] = useState({
    MainTitle: "Invoice Overview",
    cardDetails: [
      { cardTitle: "Total", cardContent: 0 },
      { cardTitle: "Paid", cardContent: 0 },
      { cardTitle: "Pending", cardContent: 0 }
    ]
  });

  //Job Overview
  useEffect(() => {
    const fetchJobSummary = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/job-summary`, {
          year: selectedYear,
          month: selectedMonth
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          const data = result.data;

          setJobSummary({
            MainTitle: "Jobs Overview (" + data.month + " " + data.year + ")",
            cardDetails: [
              { cardTitle: "Total", cardContent: data.total_jobs_posted },
              { cardTitle: "Pending", cardContent: data.jobs_pending },
              { cardTitle: "Completed", cardContent: data.jobs_completed },
              { cardTitle: "In Progress", cardContent: data.jobs_in_progress },
              { cardTitle: "Cancelled", cardContent: data.jobs_canceled }
            ]
          });
        } else {
          console.error("Job summary fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching job summary:", error);
      }
    };

    fetchJobSummary();
  }, [selectedYear, selectedMonth]);

  //Invoice Overview
  useEffect(() => {
    const fetchInvoiceSummary = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/invoice-summary`, {
          year: selectedYear,
          month: selectedMonth
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          const data = result.data;

          setInvSummary({
            MainTitle: "Invoice Overview (" + data.month + " " + data.year + ")",
            cardDetails: [
              { cardTitle: "CC Total", cardContent: data.cc_total_invoice },
              { cardTitle: "CC Paid", cardContent: data.cc_invoices_paid },
              { cardTitle: "CC Pending", cardContent: data.cc_invoices_pending },
              { cardTitle: "TC Total", cardContent: data.tc_total_invoice },
              { cardTitle: "TC Paid", cardContent: data.tc_invoices_paid },
              { cardTitle: "TC Pending", cardContent: data.tc_invoices_pending }
            ]
          });
        } else {
          console.error("Invoice summary fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching invoice summary:", error);
      }
    };

    fetchInvoiceSummary();
  }, [selectedYear, selectedMonth]);

  //Job Trend
  useEffect(() => {
    const fetchJobTrend = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/job-trend`, {
          year: selectedYear
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setJobTrendData(result.data);
        } else {
          console.error("Job trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching job trend:", error);
      }
    };

    fetchJobTrend();
  }, [selectedYear]);

  //Invoice Trend
  useEffect(() => {
    const fetchInvoiceTrend = async () => {
      setInvoiceTrendData([]);
      try {
        const response = await apiClient.post(`${dashboardPath}/invoice-trend`, {
          year: selectedYear
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setInvoiceTrendData(result.data);
        } else {
          console.error("Invoice trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching invoice trend:", error);
      }
    };

    fetchInvoiceTrend();
  }, [selectedYear]);

  //Truck Trend
  useEffect(() => {
    const fetchTruckTrend = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/truck-trend`, {
          year: selectedYear
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setTruckTrendData(result.data);
        } else {
          console.error("Truck trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching truck trend:", error);
      }
    };

    fetchTruckTrend();
  }, [selectedYear]);

  //Active Jobs
  useEffect(() => {
    const fetchActiveJobs = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const response = await apiClient.post(`${dashboardPath}/top-ccompanies`, { year: selectedYear });
        const result = response.data;
        if (result.status === "SUCCESS" && result.data) {
          setActiveJobs(result.data);
        } else {
          console.error("Active jobs fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching active jobs:", error);
      }
    };

    fetchActiveJobs();
  }, [selectedYear]);

  return (
    <main className="full-wraper dashboard-wraper">
      <div className="main-wraper">
        <div className="mi-top">
          <YearMonthSelector
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth} />
        </div>
        <div className='graph-wrapper hsgraph-wraper'>
          <div className='graph-wrapper-row1'>
            <div className='graph-wrapper-row-col'>
              <div className='jobs-wrapper-col-row'>
                <div className='overview-row hsoverview-row'>
                  <JobsOverview jobSummary={jobSummary} />
                </div>
                <div className='trend-row'>
                  <JobsTrend data={jobTrendData} />
                </div>
              </div>
              <div className='invoices-wrapper-col-row'>
                <div className='overview-row hsoverview-row'>
                  <InvoicesOverview invSummary={invSummary} />
                </div>
                <div className='trend-row'>
                  <InvoicesTrend data={invoiceTrendData} />
                </div>
              </div>
            </div>
          </div>
          <div className='graph-wrapper-row2'>
            <div className='graph-wrapper-row-col'>
              <div className='trend-row'>
                <TrucksTrend data={truckTrendData} />
              </div>
              <div className='trend-row'>
                <CCompanies companies={activeJobs} />
              </div>
            </div>
          </div>
        </div>
      </div>



    </main>
  );
};

export default Dashboard;
